import * as yup from 'yup';
import { DocumentNode } from 'graphql';
import { ExternalLink, Trash, XCircle } from 'react-feather';
import { Route, Switch, useHistory } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { gql } from '__generated__';
import { useEffect } from 'react';
import { useForm } from 'react-form-state-manager';

import {
  GetAppsForProjectQuery as GetAppsForProject,
} from '__generated__/graphql';

import { ContainerWithOptions, Option } from '../../common/Common/OptionsDropdown';
import { EditAppPermissionsForm } from './EditPermissionsForm';
import { getServerErrors, resizedImageUrl } from '../../common/helpers';
import { useNotifier } from '../../common/Notifications/NotificationProvider';
import CopyLink, { CopyLinkInput } from '../../common/Common/CopyLink';
import GetAppsForProjectQuery from './GetAppsForProjectQuery';
import UI from '../../common/UI';
import useAddAppModal from './useAddAppModal';
import useConfirmModal from '../../common/useConfirmModal';
import useLocale from '../../common/useLocale';
import useMediaDevice from '../../common/useMediaDevice';
import useMutation from '../../api/useMutation';
import useNavigationPrompt from '../../common/useNavigationPrompt';
import useProject from '../useProject';
import useQuery from '../../api/useQuery';
import useValidation from '../../common/useValidation';

type Project = GetAppsForProject['project'];
type App = Project['apps'][0];
type Authorization = App['authorizations'][0];

export const EditAppMutation = gql(`
  mutation EditApp($input: EditAppInput!) {
    editApp(input: $input) {
      ...AppFields
    }
  }
`);

export const DeleteAppMutation = gql(`
  mutation DeleteApp($app_id: ID!) {
    deleteApp(id: $app_id)
  }
`);

export const DisconnectAppMutation = gql(`
  mutation RemoveAppFromProject($project_id: ID!, $app_id: ID!) {
    removeAppFromProject(
      project: { id: $project_id },
      app: { id: $app_id }
    )
  }
`);

export interface ApiPageProps {
  projectId: string;
  appId?: string;
  refetchQueries?: DocumentNode[];
}

const ApiPage = ({ projectId, appId: activeAppId, refetchQueries = [GetAppsForProjectQuery] }: ApiPageProps) => {
  const { t } = useTranslation();

  const { data, refetch } = useQuery(GetAppsForProjectQuery, {
    variables: {
      project_id: projectId,
    },
  });

  useEffect(() => {
    refetch();
  }, [activeAppId, refetch]);

  const project = data?.project;

  const [openAddAppModal] = useAddAppModal();

  if (!project) {
    return <></>;
  }

  const hasApps = project.apps.length > 0;

  return (
    <UI.Container sc={{ compact: true, px: [0, 4] }}>
      <UI.FlexContainer sc={{ alignItems: 'center', justifyContent: 'space-between' }}>
        <UI.H3>
          {t('atleta_api')}
        </UI.H3>

        <UI.FlexContainer sc={{ spaceX: 1 }}>
          <UI.AButton
            href="https://about.atleta.cc/article/api"
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('documentation')}
            {' '}
            <UI.Icon>
              <ExternalLink />
            </UI.Icon>
          </UI.AButton>
          <UI.Button
            onClick={openAddAppModal}
            sc={{ brand: 'secondary' }}
            style={{ whiteSpace: 'nowrap' }}
          >
            {t('add_app')}
          </UI.Button>
        </UI.FlexContainer>
      </UI.FlexContainer>

      <UI.InputDescription sc={{ my: 3 }}>
        {t('app_access_description')}
      </UI.InputDescription>

      {hasApps && (
        <UI.GridContainer sc={{ gutter: 0.5 }}>
          {project.apps.map((app) => (
            <UI.CollapsibleNavCard
              to={`/${project.id}/project/api/${activeAppId !== app.id ? app.id : ''}`}
              open={app.id === activeAppId}
              muted={!!activeAppId && app.id !== activeAppId}
              preview={(
                <AppPreview app={app} project={project} />
              )}
              body={(
                <UI.FormGrid sc={{ columns: ['1fr', '1fr 3fr'], gutter: 1.5 }}>
                  <UI.Nav>
                    <UI.NavLink
                      to={`/${project.id}/project/api/${app.id}`}
                      exact
                      resetScrollPosition={false}
                    >
                      {t('basic_info')}
                    </UI.NavLink>
                    <UI.NavLink
                      to={`/${project.id}/project/api/${app.id}/projects`}
                      resetScrollPosition={false}
                    >
                      {t('connected_projects')}
                    </UI.NavLink>
                  </UI.Nav>
                  <UI.Div>
                    <Switch>
                      <Route path="/:projectId/project/api/:appId/projects">
                        <EditAppConnectedProjectsForm app={app} refetchQueries={refetchQueries} />
                      </Route>
                      <Route path="/:projectId/project/api/:appId">
                        <EditAppInfoForm app={app} refetchQueries={refetchQueries} />
                      </Route>
                    </Switch>
                  </UI.Div>
                </UI.FormGrid>
              )}
              id={`AppCard_${app.id}`}
              key={app.id}
            />
          ))}
        </UI.GridContainer>
      )}
      {!hasApps && (
        <UI.Card sc={{ textAlign: 'center', background: 'gray.50' }}>
          {t('no_apps_yet')}
        </UI.Card>
      )}
    </UI.Container>
  );
};

interface AppPreviewProps {
  app: App;
  project: Project;
  refetchQueries?: DocumentNode[];
}

const AppPreview = ({
  app, project,
  refetchQueries = [GetAppsForProjectQuery],
}: AppPreviewProps) => {
  const { t } = useTranslation();
  const { formatDate, parseDate } = useLocale();
  const device = useMediaDevice();
  const notifier = useNotifier();
  const history = useHistory();

  const [deleteApp] = useMutation(
    DeleteAppMutation,
    {
      variables: {
        app_id: app.id,
      },
      refetchQueries,
      onCompleted: () => {
        notifier.success(t('notifications.app_removed'));
        history.push(`/${project.id}/project/api`);
      },
    },
  );

  const [confirmDelete] = useConfirmModal({
    title: t('delete_app'),
    body: (
      <Trans
        i18nKey="confirm_delete_app"
        values={{
          name: app.name,
        }}
      >
        <UI.Strong />
      </Trans>
    ),
    onConfirm: () => deleteApp(),
  }, []);

  return (
    <ContainerWithOptions
      name={app.name}
      alignVertical="center"
      renderOptions={() => (
        <Option
          icon={<Trash />}
          label={t('delete_app')}
          aria-label={t('delete_app')}
          onClick={() => confirmDelete()}
        />
      )}
    >
      <UI.GridContainer
        sc={{
          columns: device.isSmall ? '30px 2fr 1fr' : '30px 2fr 1fr 1fr',
          gutter: 0.5,
          alignVertical: 'center',
        }}
      >
        <UI.Div>
          <UI.ProfilePic
            sc={{
              background: 'secondary',
              fit: 'cover',
              size: 30,
            }}
          >
            {app.logo_url && <img src={resizedImageUrl(app.logo_url, 300)} alt={app.name} />}
            {!app.logo_url && <UI.Span><UI.Span>{app.name.charAt(0).toUpperCase()}</UI.Span></UI.Span>}
          </UI.ProfilePic>
        </UI.Div>
        <UI.H4 sc={{ inlineBlock: true }}>
          <UI.Truncate>
            {app.name}
          </UI.Truncate>
        </UI.H4>
        {!device.isSmall && (
          <UI.Div sc={{ color: 'gray.500' }}>
            {formatDate(parseDate(app.created_at, { timezone: 'UTC' }), { format: 'display_compact_date' })}
          </UI.Div>
        )}
        <UI.Div sc={{ textAlign: 'right' }}>
          <UI.Label sc={{ fontWeight: 500, small: true }}>
            {app.authorizations.length === 1 && app.authorizations[0].project.id === project.id ? (
              t('local_app')
            ) : (
              t('n_projects', { count: app.authorizations.length })
            )}
          </UI.Label>
        </UI.Div>
      </UI.GridContainer>
    </ContainerWithOptions>
  );
};

const editAppInfoSchema = yup.object().shape({
  name: yup.string().required(),
  description: yup.string().max(255).nullable(),
  permissions: yup.array().min(1).required(),
});

interface EditAppFormProps {
  app: App;
  refetchQueries?: DocumentNode[];
}

const EditAppInfoForm = ({ app, refetchQueries }: EditAppFormProps) => {
  const { t } = useTranslation();
  const notifier = useNotifier();

  const getValues = (app: App) => ({
    name: app.name,
    description: app.description,
    permissions: app.requested_permissions || [],
  });

  const form = useForm({
    values: getValues(app),
  });

  useNavigationPrompt(form.changed());

  const [editApp, { error, loading }] = useMutation(
    EditAppMutation,
    {
      variables: {
        input: {
          id: app.id,
          name: form.values.name,
          description: form.values.description,
          requested_permissions: form.values.permissions,
        },
      },
      refetchQueries,
      onCompleted: ({ editApp }) => {
        form.reset(getValues(editApp));
        notifier.success(t('notifications.app_saved'));
      },
    },
  );

  const { valid, errors } = useValidation({
    schema: editAppInfoSchema,
    values: form.values,
    externalErrors: getServerErrors(error),
  });

  return (
    <UI.Form onSubmit={() => editApp()}>
      <UI.FormGrid>
        <UI.Legend>
          {t('about')}
        </UI.Legend>

        <UI.InputGroup sc={{ valid: !errors.name, touched: form.touched.name }}>
          <UI.InputLabel htmlFor="AppName">
            {t('name')}
            {' '}
            <UI.RequiredMark />
          </UI.InputLabel>
          <UI.InputDescription>
            {t('app_name_description')}
          </UI.InputDescription>
          <UI.DebouncedInput
            {...form.text('name')}
            name="name"
            id="AppName"
          />
          <UI.ErrorMessages attribute={t('name')} errors={errors.name} />
        </UI.InputGroup>

        <UI.InputGroup sc={{ valid: !errors.description, touched: form.touched.description }}>
          <UI.InputLabel htmlFor="AppDescription">
            {t('description')}
            {' '}
            <UI.Span sc={{ fontWeight: 500, small: true, muted: true }}>
              (
              {form.values.description?.length || 0}
              /255)
            </UI.Span>
          </UI.InputLabel>
          <UI.TextArea
            {...form.textarea('description')}
            id="AppDescription"
          />
          <UI.ErrorMessages attribute={t('description')} errors={errors.description} />
        </UI.InputGroup>

        <UI.HR />

        <UI.Legend>
          {t('permissions_title')}
        </UI.Legend>

        <EditAppPermissionsForm
          form={form}
          errors={errors}
          inputLabel={t('requested_permissions')}
          inputDescription={t('requested_permissions_description')}
        />

        <UI.HR />

        <UI.FormButtons
          form={form}
          submitLabel={t('save_app')}
          loading={loading}
          valid={valid}
        />
      </UI.FormGrid>
    </UI.Form>
  );
};

const EditAppConnectedProjectsForm = ({ app, refetchQueries }: EditAppFormProps) => {
  const { t } = useTranslation();

  return (
    <UI.FormGrid>
      <UI.Div>
        <UI.Legend sc={{ mb: 1 }}>
          {t('connect_with_organisers')}
        </UI.Legend>
        <UI.InputDescription>
          {t('connect_url_description')}
        </UI.InputDescription>
      </UI.Div>

      {app.connect_url && (
        <CopyLinkInput value={app.connect_url} />
      )}

      {!app.connect_url && (
        <UI.Warning>
          {t('app_verify_warning')}
        </UI.Warning>
      )}

      <UI.HR />

      <UI.Div>
        <UI.Legend sc={{ mb: 1 }}>
          {t('connected_projects')}
        </UI.Legend>
        <UI.InputDescription>
          {t('connected_projects_description')}
        </UI.InputDescription>
      </UI.Div>
      <UI.Div>
        <UI.GridContainer sc={{ columns: '64px 3fr 1fr 35px', gutter: 0.5, fontWeight: 500 }}>
          <UI.Div>
            {t('id')}
          </UI.Div>
          <UI.Div>
            {t('name')}
          </UI.Div>
          <UI.Div>
            {t('date')}
          </UI.Div>
        </UI.GridContainer>
        <UI.HR sc={{ my: 1 }} />
        {app.authorizations.map((authorization) => (
          <UI.Div key={authorization.id}>
            <ConnectedProject authorization={authorization} app={app} refetchQueries={refetchQueries} />
            <UI.HR sc={{ dashed: true, my: 1 }} />
          </UI.Div>
        ))}
      </UI.Div>
    </UI.FormGrid>
  );
};

interface ConnectedProjectProps {
  authorization: Authorization;
  refetchQueries?: DocumentNode[];
  app: App;
}

const ConnectedProject = ({ authorization, app, refetchQueries }: ConnectedProjectProps) => {
  const { t } = useTranslation();
  const { formatDate, parseDate } = useLocale();
  const project = useProject();
  const notifier = useNotifier();

  const [disconnectApp] = useMutation(
    DisconnectAppMutation,
    {
      variables: {
        project_id: authorization.project.id,
        app_id: app.id,
      },
      refetchQueries,
      onCompleted: () => {
        notifier.success(t('notifications.app_removed'));
      },
    },
  );

  const missingPermissions = app.requested_permissions.filter(
    (permission) => !authorization.permissions.includes(permission),
  );

  const [confirmDisconnect] = useConfirmModal({
    title: t('disconnect'),
    body: (
      <Trans
        i18nKey="confirm_disconnect_app"
        values={{
          name: app.name,
          project: authorization.project.organisation_name,
        }}
      >
        <UI.Strong />
      </Trans>
    ),
    onConfirm: () => disconnectApp(),
  }, []);

  return (
    <ContainerWithOptions
      name={authorization.project.organisation_name}
      renderOptions={authorization.project.id === project.id ? null : () => (
        <Option
          icon={<XCircle />}
          label={t('disconnect')}
          aria-label={t('disconnect')}
          onClick={() => confirmDisconnect()}
        />
      )}
    >
      <UI.GridContainer
        sc={{
          columns: `64px 3fr 1fr${authorization.project.id === project.id ? ' 35px' : ''}`,
          gutter: 0.5,
        }}
      >
        <UI.Div>
          <CopyLink value={authorization.project.id}>
            {authorization.project.id}
          </CopyLink>
        </UI.Div>
        <UI.Div>
          {authorization.project.organisation_name}
          {missingPermissions.length > 0 && (
            <UI.Warning sc={{ mt: 1 }}>
              <UI.H5>
                {t('missing_permissions')}
              </UI.H5>
              {authorization.permissions.map((permission) => t(`permissions.${permission}`)).join(', ')}
            </UI.Warning>
          )}
        </UI.Div>
        <UI.Div>
          {formatDate(
            parseDate(authorization.created_at, { timezone: 'UTC' }),
            { format: 'display_compact_date' },
          )}
        </UI.Div>
      </UI.GridContainer>
    </ContainerWithOptions>
  );
};

export default ApiPage;
